import { useCallback, useEffect, useMemo, useState } from 'react'

import { getLocalstorage, setLocalstorage } from '~/helpers'
import { MIN_VISIBLE_CARDS } from '~/modules/retailMedia/constants/card'

import { useCardVisibilityStorageKey } from './useCardVisibilityStorage'

const unique = <T>(arr: T[]): T[] => [...new Set(arr)]

const normalizeHidden = <T extends string>({
  order,
  hidden
}: {
  order: T[]
  hidden: T[]
}): T[] => {
  const validOrderSet = new Set(order)
  const hiddenSet = new Set(hidden.filter(item => validOrderSet.has(item)))
  const minVisible = Math.min(MIN_VISIBLE_CARDS, order.length)
  const maxHidden = Math.max(0, order.length - minVisible)

  return order.filter(item => hiddenSet.has(item)).slice(0, maxHidden)
}

const sortByOrder = <T extends string, TCard extends CardSelectorItem<T>>(
  cards: TCard[],
  order: T[]
): TCard[] => {
  const orderMap = new Map(order.map((type, index) => [type, index]))

  return [...cards].sort((a, b) => {
    const aIndex = orderMap.get(a.type) ?? Number.MAX_SAFE_INTEGER
    const bIndex = orderMap.get(b.type) ?? Number.MAX_SAFE_INTEGER

    return aIndex - bIndex
  })
}

const parsePreference = <T extends string>(
  storageKey: string | null,
  defaultOrder: T[],
  defaultHidden: T[]
): CardSelectorPreference<T> => {
  const fallback: CardSelectorPreference<T> = {
    order: [...defaultOrder],
    hidden: [...defaultHidden]
  }

  if (!storageKey) return fallback

  const raw = getLocalstorage(storageKey)

  if (!raw) return fallback

  try {
    const parsed = JSON.parse(raw)
    const knownSet = new Set(defaultOrder)
    const isKnown = (item: unknown): item is T =>
      typeof item === 'string' && knownSet.has(item as T)

    const storedOrder = Array.isArray(parsed?.order)
      ? (parsed.order as unknown[]).filter(isKnown)
      : []

    // merge stored order with defaults so new cards appear at the end
    const order = unique([...storedOrder, ...defaultOrder])

    const storedHidden = Array.isArray(parsed?.hidden)
      ? (parsed.hidden as unknown[]).filter(isKnown)
      : []
    const storedOrderSet = new Set(storedOrder)

    return {
      order,
      hidden: normalizeHidden({
        order,
        hidden: unique([
          ...storedHidden,
          ...defaultHidden.filter(type => !storedOrderSet.has(type))
        ])
      })
    }
  } catch {
    return fallback
  }
}

export function useCardVisibility<
  T extends string,
  TCard extends CardSelectorItem<T>
>({
  cards,
  screenName,
  defaultVisible
}: UseCardVisibilityOptions<T, TCard>): UseCardVisibilityReturn<T, TCard> {
  const defaultOrder = useMemo(() => cards.map(c => c.type), [cards])

  const defaultHidden = useMemo(
    () => defaultOrder.filter(type => !defaultVisible.includes(type)),
    [defaultOrder, defaultVisible]
  )

  const storageKey = useCardVisibilityStorageKey(screenName)

  const [preferences, setPreferences] = useState<CardSelectorPreference<T>>(
    () => parsePreference(storageKey, defaultOrder, defaultHidden)
  )

  useEffect(() => {
    if (storageKey) {
      setLocalstorage(storageKey, JSON.stringify(preferences))
    }
  }, [preferences, storageKey])

  const cardsToCustomize = useMemo(
    () => sortByOrder(cards, preferences.order),
    [cards, preferences.order]
  )

  const defaultCardsToCustomize = useMemo(
    () => sortByOrder(cards, defaultOrder),
    [cards, defaultOrder]
  )

  const hiddenCards = useMemo(() => {
    const types = cardsToCustomize.map(c => c.type)
    const knownOrder = new Set(preferences.order)

    return normalizeHidden({
      order: types,
      hidden: unique([
        ...types.filter(type => preferences.hidden.includes(type)),
        ...defaultHidden.filter(type => !knownOrder.has(type))
      ])
    })
  }, [cardsToCustomize, defaultHidden, preferences.hidden, preferences.order])

  const defaultHiddenCards = useMemo(
    () =>
      normalizeHidden({
        order: defaultCardsToCustomize.map(c => c.type),
        hidden: defaultCardsToCustomize
          .map(c => c.type)
          .filter(type => defaultHidden.includes(type))
      }),
    [defaultCardsToCustomize, defaultHidden]
  )

  const cardsToShow = useMemo(
    () => cardsToCustomize.filter(c => !hiddenCards.includes(c.type)),
    [cardsToCustomize, hiddenCards]
  )

  const saveCustomization = useCallback(
    ({ order, hidden }: { order: T[]; hidden: T[] }) => {
      const knownSet = new Set(cards.map(c => c.type))
      const validOrder = unique(order.filter(t => knownSet.has(t)))
      const nextOrder =
        validOrder.length > 0
          ? unique([...validOrder, ...cards.map(c => c.type)])
          : [...knownSet]
      const nextHidden = normalizeHidden({
        order: nextOrder,
        hidden: hidden.filter(t => knownSet.has(t))
      })

      setPreferences({ order: nextOrder, hidden: nextHidden })
    },
    [cards]
  )

  const resetCustomization = useCallback(() => {
    setPreferences({
      order: [...defaultOrder],
      hidden: [...defaultHidden]
    })
  }, [defaultOrder, defaultHidden])

  return {
    cardsToShow,
    cardsToCustomize,
    hiddenCards,
    defaultCardsToCustomize,
    defaultHiddenCards,
    saveCustomization,
    resetCustomization
  }
}
